import { useState, useEffect } from 'react'
import { Mail, ChevronDown, ChevronRight } from 'lucide-react'
import api from '../../services/api'
import { formatDateTime } from '../../utils/helpers'

/**
 * Every email the agent has been handed, and what became of it.
 *
 * WHY THIS EXISTS. "I emailed my deliverable and nothing happened" was unanswerable: the record
 * was a table with no screen, so the only way to look was a database query over SSH. Somebody has
 * to be able to see what arrived, whether it was understood, and what was said back — otherwise
 * the email door is the one part of ProTrack where a person's work can disappear and nobody can
 * say where it went.
 *
 * Collapsed by default. It is a place to look when something is wrong, not something to read
 * every day, and an Admin's dashboard is crowded enough already.
 */

const OUTCOMES = {
  done:           { label: 'Submitted',        cls: 'bg-green-100 text-green-800' },
  proposed:       { label: 'Waiting on them',  cls: 'bg-violet-100 text-violet-800' },
  asked:          { label: 'Asked a question', cls: 'bg-amber-100 text-amber-800' },
  answered:       { label: 'Answered',         cls: 'bg-sky-100 text-sky-800' },
  cancelled:      { label: 'Cancelled by them', cls: 'bg-gray-100 text-gray-700' },
  superseded:     { label: 'Replaced by a later reply', cls: 'bg-gray-100 text-gray-600' },
  unknown_sender: { label: 'Unknown sender',   cls: 'bg-gray-200 text-gray-700' },
  error:          { label: 'Failed',           cls: 'bg-red-100 text-red-800' },
}

export default function EmailIntakeLog() {
  const [items, setItems] = useState(null)
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState(null)

  useEffect(() => {
    api.get('/agent/inbox/messages', { params: { limit: 50 } })
      .then(r => setItems(r.data?.items || []))
      // No email door on this stack, or not allowed to see it. Either way, show nothing rather
      // than an error about a feature they may not know exists.
      .catch(() => setItems([]))
  }, [])

  if (!items || items.length === 0) return null

  return (
    <div className="bg-white border border-gray-200 rounded-xl mb-6">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <Mail className="w-4 h-4 text-gray-500" />
          Work sent in by email
          <span className="text-xs font-normal text-gray-500">({items.length})</span>
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-gray-100 divide-y divide-gray-100">
          {items.map(m => {
            const o = OUTCOMES[m.outcome] || { label: m.outcome, cls: 'bg-gray-100 text-gray-700' }
            const isOpen = expanded === m.id
            return (
              <div key={m.id} className="px-5 py-3">
                <button
                  onClick={() => setExpanded(isOpen ? null : m.id)}
                  className="w-full flex items-start gap-2 text-left"
                >
                  {isOpen ? <ChevronDown className="w-3.5 h-3.5 mt-1 text-gray-400 shrink-0" />
                          : <ChevronRight className="w-3.5 h-3.5 mt-1 text-gray-400 shrink-0" />}
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-gray-900 truncate">
                      {m.subject || '(no subject)'}
                    </span>
                    <span className="block text-[11px] text-gray-500">
                      {m.from_email}{m.received_at ? ` · ${formatDateTime(m.received_at)}` : ''}
                    </span>
                  </span>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${o.cls}`}>
                    {o.label}
                  </span>
                </button>

                {isOpen && (
                  <div className="mt-2 ml-5 space-y-2">
                    {/* What they wrote. Usually the whole explanation for what the agent then did. */}
                    {m.body && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">They wrote</p>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-2">{m.body}</p>
                      </div>
                    )}
                    {m.reply_text && (
                      <div>
                        <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-0.5">We replied</p>
                        <p className="text-xs text-gray-700 whitespace-pre-wrap bg-blue-50 rounded-lg p-2">{m.reply_text}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
